import { Reveal } from "@/components/Reveal";
import { KiteMark } from "@/components/Logo";
import { scrollToId } from "@/lib/api";

const PilotKiteSvg = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} fill="none" aria-hidden="true">
    <path d="M32 4 L54 26 L32 48 L10 26 Z" fill="#14355c" />
    <path d="M32 4 L54 26 L32 26 Z" fill="#f0b52f" />
    <path d="M32 4 L32 26 L10 26 Z" fill="#2e7f86" />
    <path d="M32 26 L54 26 L32 48 Z" fill="#cf4b26" />
    <path d="M32 4 L54 26 L32 48 L10 26 Z" stroke="#14355c" strokeWidth="2" />
    <path d="M32 4 L32 48 M10 26 L54 26" stroke="#14355c" strokeWidth="1.6" />
    <path d="M32 48 C 28 54, 36 58, 31 62" stroke="#14355c" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const FishKiteSvg = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} fill="none" aria-hidden="true">
    <path d="M12 32 C 20 18, 42 16, 52 26 L 60 32 L 52 38 C 42 48, 20 46, 12 32 Z" fill="#2e7f86" stroke="#14355c" strokeWidth="2" />
    <circle cx="24" cy="30" r="2.4" fill="#f7efdc" />
    <path d="M30 24 C 36 28, 36 36, 30 40" stroke="#f0b52f" strokeWidth="2" fill="none" />
    <path d="M38 24 C 44 28, 44 36, 38 40" stroke="#f0b52f" strokeWidth="2" fill="none" />
    <path d="M12 32 L 4 25 M12 32 L 4 39" stroke="#cf4b26" strokeWidth="2.6" strokeLinecap="round" />
    <path d="M28 46 C 32 52, 40 52, 44 56" stroke="#cf4b26" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const StuntKiteSvg = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} fill="none" aria-hidden="true">
    <path d="M32 8 L58 42 L32 32 L6 42 Z" fill="#cf4b26" stroke="#14355c" strokeWidth="2" />
    <path d="M32 8 L32 32 M6 42 L32 32 L58 42" stroke="#14355c" strokeWidth="1.6" />
    <path d="M32 32 L 20 56 M32 32 L 44 56" stroke="#14355c" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="18" cy="57" r="3.2" fill="#f0b52f" stroke="#14355c" strokeWidth="1.5" />
    <circle cx="46" cy="57" r="3.2" fill="#f0b52f" stroke="#14355c" strokeWidth="1.5" />
  </svg>
);

const COLLECTION = [
  { icon: PilotKiteSvg, label: "Pilot Kites", testid: "collection-item-pilot" },
  { icon: FishKiteSvg, label: "Character Kites", testid: "collection-item-character" },
  { icon: StuntKiteSvg, label: "Stunt Kites", testid: "collection-item-stunt" },
];

const TeamCollection = ({ content, members }) => (
  <section id="team" className="relative py-24 sm:py-32 bg-paper overflow-x-clip" data-testid="team-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid md:grid-cols-12 gap-12 items-center">
        <Reveal className="md:col-span-4 flex justify-center">
          <div className="relative rounded-2xl overflow-hidden border-4 border-verm shadow-xl shadow-navy/20 bg-marigold/20 p-2 max-w-xs">
            <img
              src={content.team_image}
              alt="Folk art of the Wind Skaters team flying kites"
              className="w-full h-80 sm:h-96 object-cover rounded-lg"
              data-testid="team-image"
            />
          </div>
        </Reveal>

        <Reveal delay={0.1} className="md:col-span-4">
          <p className="font-script text-verm text-4xl sm:text-5xl" data-testid="team-title">
            {content.team_title}
          </p>
          <span className="block mt-4 h-1.5 w-24 rounded-full bg-gradient-to-r from-verm via-marigold to-kite" />
          <p className="mt-6 font-body text-navy/85 text-base sm:text-lg leading-relaxed" data-testid="team-text">
            {content.team_text}
          </p>
          <button
            onClick={() => scrollToId("#contact")}
            data-testid="team-cta-meet"
            className="mt-7 inline-flex items-center gap-2 bg-navy text-paper font-semibold px-7 py-3 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300"
          >
            Meet Our Team
            <KiteMark className="w-5 h-5" />
          </button>
        </Reveal>

        <Reveal delay={0.2} className="md:col-span-4">
          <div className="bg-paperdeep/60 border-2 border-navy/15 rounded-2xl p-7 sm:p-8 h-full">
            <p className="font-script text-kite text-4xl sm:text-5xl" data-testid="collection-title">
              {content.collection_title}
            </p>
            <p className="mt-4 font-body text-navy/80 text-sm leading-relaxed" data-testid="collection-text">
              {content.collection_text}
            </p>
            <div className="mt-6 grid grid-cols-3 gap-3" data-testid="collection-items">
              {COLLECTION.map((item) => (
                <div
                  key={item.label}
                  data-testid={item.testid}
                  className="flex flex-col items-center text-center gap-2 rounded-xl border-2 border-transparent hover:border-marigold/60 hover:bg-paper transition-colors duration-300 py-3 px-1"
                >
                  <item.icon className="w-12 h-12" />
                  <span className="font-body font-semibold text-navy text-xs sm:text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      {members?.length ? (
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="team-members-grid">
          {members.slice(0, 6).map((m, i) => (
            <Reveal key={m.id} delay={(i % 3) * 0.08}>
              <div
                className="h-full bg-paperdeep/50 border-2 border-navy/15 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg hover:shadow-navy/10 transition-all duration-300"
                data-testid="team-member-card"
              >
                <div className="flex items-center gap-4">
                  <span className="relative w-16 h-16 rounded-full folk-ring p-1 shrink-0">
                    {m.photo_url ? (
                      <img
                        src={m.photo_url}
                        alt={m.name}
                        className="w-full h-full object-cover rounded-full"
                        data-testid="team-member-photo"
                      />
                    ) : (
                      <span className="w-full h-full rounded-full bg-marigold grid place-items-center">
                        <KiteMark className="w-8 h-8" />
                      </span>
                    )}
                  </span>
                  <div>
                    <p className="font-display font-bold text-navy text-xl leading-tight" data-testid="team-member-name">
                      {m.name}
                    </p>
                    {m.role ? (
                      <p className="font-body text-verm text-xs font-bold tracking-[0.18em] uppercase mt-1" data-testid="team-member-role">
                        {m.role}
                      </p>
                    ) : null}
                  </div>
                </div>
                {m.story ? (
                  <p className="mt-4 font-body text-navy/80 text-sm leading-relaxed" data-testid="team-member-story">
                    {m.story}
                  </p>
                ) : null}
              </div>
            </Reveal>
          ))}
        </div>
      ) : null}
    </div>
  </section>
);

export default TeamCollection;
